/**
 * Poll the inbox aggregator on a short cadence, diff against the last
 * snapshot, and when something new lands:
 *   1. play /notification.wav
 *   2. push a notification-centre entry with a link to /inbox
 *
 * Mounted once in DashboardLayout so alerts fire from any page.
 * Browsers require a prior user interaction before audio will play —
 * we swallow that error silently so the rest of the hook keeps
 * working; on first interaction the next ping will be audible.
 */

import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { listInbox, type InboxThread } from '@/api/inbox'
import { notify } from '@/lib/notify'
import { useAuthStore } from '@/store/auth'

const POLL_MS = 15_000
const CHAT_SOUND_URL = '/notification.wav'
const EMAIL_SOUND_URL = '/email.mp3'
const SOUND_MUTED_KEY = 'admindash-v2-alerts-muted'

export function useInboxAlerts() {
  // Snapshot of thread_key → last_message_at the last time we polled.
  // First poll just seeds this; only subsequent polls can trigger an
  // alert, so a freshly-opened tab doesn't ping for every historical
  // thread.
  const seen = useRef<Map<string, string> | null>(null)
  const chatAudio = useRef<HTMLAudioElement | null>(null)
  const emailAudio = useRef<HTMLAudioElement | null>(null)
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => !!s.token)
  const userId = useAuthStore((s) => s.user?.id ?? '')

  useEffect(() => {
    const a = new Audio(CHAT_SOUND_URL)
    a.preload = 'auto'
    a.volume = 0.6
    chatAudio.current = a
    const e = new Audio(EMAIL_SOUND_URL)
    e.preload = 'auto'
    e.volume = 0.7
    emailAudio.current = e
  }, [])

  useQuery({
    queryKey: ['inbox-alerts'],
    enabled: isAuthenticated,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const r = await listInbox({ source: 'all', limit: 100 })
      const threads = r.threads ?? []

      // First successful poll seeds the snapshot and alerts nothing —
      // the operator isn't interested in "alerts" for threads that
      // were already there when they loaded the page.
      if (seen.current === null) {
        seen.current = new Map()
        for (const t of threads) seen.current.set(t.key, t.last_message_at || t.created_at || '')
        return r
      }

      const prev = seen.current
      const fresh: InboxThread[] = []
      // Track participants we've already alerted about in this cycle
      // to avoid duplicate alerts when one visitor has multiple sessions.
      const alertedParticipants = new Set<string>()
      for (const t of threads) {
        const lastSeen = prev.get(t.key)
        // New thread (not in prev) OR activity advanced (new message)
        const tLastMsg = t.last_message_at || t.created_at || ''
        if (lastSeen === undefined || tLastMsg > lastSeen) {
          // Deduplicate by participant (email/name) so multiple sessions
          // from the same visitor don't fire separate alerts.
          const participant = t.participant ?? t.email ?? t.key
          if (!alertedParticipants.has(participant)) {
            alertedParticipants.add(participant)
            fresh.push(t)
          }
        }
      }

      // Always update snapshot even when no alert fires — prevents
      // the same ping repeating every cycle if the operator ignores
      // the notification.
      const nextSeen = new Map<string, string>()
      for (const t of threads) nextSeen.set(t.key, t.last_message_at || t.created_at || '')
      seen.current = nextSeen

      if (fresh.length === 0) return r

      // Unified ownership filter: only alert for threads I own or unassigned
      const toNotify = fresh.filter((t) => {
        if (!t.owner_id) return true        // unassigned = everyone sees it
        return t.owner_id === userId        // assigned = only owner
      })

      if (toNotify.length === 0) return r

      const onInboxPage = location.pathname.startsWith('/inbox')
      const hasEmail = toNotify.some((t) => t.source === 'email')
      const hasChat = toNotify.some((t) => t.source !== 'email')

      if (hasEmail) {
        playSound(emailAudio.current)
        showBrowserNotifications(toNotify.filter((t) => t.source === 'email'))
      }
      if (hasChat && !onInboxPage) {
        playSound(chatAudio.current)
        showBrowserNotifications(toNotify.filter((t) => t.source !== 'email'))
      }

      // Fire toast + record in notification centre.
      for (const t of toNotify.slice(0, 10)) {
        const href = t.source === 'email' && t.console_user_id
          ? `/workspace/customer/${t.console_user_id}`
          : '/inbox'
        const title = t.source === 'email'
          ? `Email reply · ${t.participant}`
          : `${sourceLabel(t.source)} · ${t.participant}`
        const tone = t.source === 'email' ? 'warn' : 'info'
        const fn = tone === 'warn' ? notify.warn : notify.info
        fn(title, {
          description: t.preview ?? t.email ?? undefined,
          href,
          source: t.source === 'email' ? 'email' : 'inbox',
        })
      }

      return r
    },
  })
}

function playSound(audio: HTMLAudioElement | null) {
  if (!audio) return
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(SOUND_MUTED_KEY)) {
    return
  }
  audio.currentTime = 0
  // The returned promise rejects with NotAllowedError until the page
  // has had a user gesture. Swallow — the operator will hear the
  // next alert after their first click.
  const p = audio.play()
  if (p && typeof p.catch === 'function') p.catch(() => {})
}

/** Fire a native OS notification when permitted; no prompts here,
 * the user has to grant permission via the (future) Settings toggle. */
function showBrowserNotifications(fresh: InboxThread[]) {
  if (typeof window === 'undefined') return
  const N = (window as unknown as { Notification?: typeof Notification }).Notification
  if (!N || N.permission !== 'granted') return
  // De-noise — one OS toast summarising, not ten.
  const title =
    fresh.length === 1
      ? `New ${fresh[0].source} · ${fresh[0].participant}`
      : `${fresh.length} new inbox messages`
  const body =
    fresh.length === 1
      ? fresh[0].preview ?? fresh[0].email ?? ''
      : fresh
          .slice(0, 3)
          .map((t) => `${sourceLabel(t.source)} · ${t.participant}`)
          .join('\n')
  try {
    const n = new N(title, { body, tag: 'admindash-inbox', silent: false })
    n.onclick = () => {
      window.focus()
      window.location.href = '/inbox'
      n.close()
    }
  } catch {
    /* ignore */
  }
}

function sourceLabel(s: InboxThread['source']): string {
  return s === 'chat' ? 'Chat' : s === 'discord' ? 'Discord' : 'Email'
}

/* ───────── Mute toggle (wired to the bell dropdown) ───────── */

export function isInboxMuted(): boolean {
  if (typeof window === 'undefined') return false
  return !!window.sessionStorage.getItem(SOUND_MUTED_KEY)
}

export function setInboxMuted(muted: boolean): void {
  if (typeof window === 'undefined') return
  if (muted) window.sessionStorage.setItem(SOUND_MUTED_KEY, '1')
  else window.sessionStorage.removeItem(SOUND_MUTED_KEY)
}

/** Request browser Notification permission on first user click. */
export async function requestInboxNotifications(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const N = (window as unknown as { Notification?: typeof Notification }).Notification
  if (!N) return false
  if (N.permission === 'granted') return true
  if (N.permission === 'denied') return false
  try {
    const r = await N.requestPermission()
    return r === 'granted'
  } catch {
    return false
  }
}
